import {
  Box,
  Button,
  Checkbox,
  Group,
  Menu,
  Radio,
  Stack,
  Text,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LazyLoadImage } from './lazy-load-image';
import classes from './select-image-slider.module.css';

const SLIDE_WIDTH = 200;
const SLIDE_GAP = 8;
const SCROLL_STEP = SLIDE_WIDTH + SLIDE_GAP;
const LAZY_LOAD_BATCH = 25;
const DRAG_THRESHOLD_PX = 5;

type InputProps =
  | {
      multiSelect: true;
      onChange: (index: string[]) => void;
      value: string[];
      onSubValueChange?: (index: string) => void;
      subValue?: string;
    }
  | {
      multiSelect?: false;
      onChange: (index: string) => void;
      value: string;
      onSubValueChange?: (values: string[]) => void;
      subValue?: string[];
    };

/* eslint-disable-next-line */
export type SelectImageSliderProps = {
  options: {
    valueLabel: string;
    src: string;
    value: string;
    manual_check: boolean;
  }[];
  draggable?: boolean;
} & InputProps;

export function SelectImageSlider(props: SelectImageSliderProps) {
  const { draggable = true } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ clientX: number; scrollLeft: number } | null>(null);
  const pointerDownRef = useRef<{
    clientX: number;
    clientY: number;
    scrollLeft: number;
    pointerId: number;
  } | null>(null);
  const [slidesInView, setSlidesInView] = useState<Set<number>>(new Set());
  const [visibleSlides, setVisibleSlides] = useState(() => {
    const selectedIndex = props.options.findIndex((o) => o.value === props.value);
    const take = selectedIndex > 25 ? selectedIndex + 10 : LAZY_LOAD_BATCH;
    return props.options.slice(0, take);
  });
  const hasMoreToLoad = visibleSlides.length < props.options.length;
  const lastCardRef = useRef<HTMLElement | null>(null);

  const subMode = useMemo(() => {
    if (props.multiSelect && props.subValue) {
      return 'radio';
    }
    if (!props.multiSelect && props.subValue) {
      return 'checkbox';
    }
    return null;
  }, [props.multiSelect, props.subValue]);

  const SCROLL_END_TOLERANCE = 2;

  const updateScrollButtons = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const isRtl = getComputedStyle(el).direction === 'rtl';
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }
    if (isRtl) {
      setCanScrollPrev(scrollLeft < -SCROLL_END_TOLERANCE);
      setCanScrollNext(scrollLeft > -maxScroll + SCROLL_END_TOLERANCE);
    } else {
      setCanScrollPrev(scrollLeft > SCROLL_END_TOLERANCE);
      setCanScrollNext(scrollLeft < maxScroll - SCROLL_END_TOLERANCE);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons);
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      ro.disconnect();
    };
  }, [updateScrollButtons, visibleSlides.length]);

  const scrollBy = useCallback((direction: 'prev' | 'next') => {
    const el = containerRef.current;
    if (!el) return;
    const isRtl = getComputedStyle(el).direction === 'rtl';
    const step = direction === 'next' ? SCROLL_STEP : -SCROLL_STEP;
    el.scrollBy({ left: isRtl ? -step : step, behavior: 'smooth' });
  }, []);

  const onPrevClick = useCallback(() => {
    if (!canScrollPrev) return;
    scrollBy('prev');
  }, [canScrollPrev, scrollBy]);

  const onNextClick = useCallback(() => {
    if (!canScrollNext) return;
    scrollBy('next');
  }, [canScrollNext, scrollBy]);

  useEffect(() => {
    if (!hasMoreToLoad) return;
    const root = containerRef.current;
    const el = lastCardRef.current;
    if (!root || !el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleSlides((current) => {
          if (current.length >= props.options.length) return current;
          return props.options.slice(
            0,
            Math.min(current.length + LAZY_LOAD_BATCH, props.options.length),
          );
        });
      },
      { root, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreToLoad, props.options, visibleSlides.length]);

  const inViewObserverRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    inViewObserverRef.current = new IntersectionObserver(
      (entries) => {
        setSlidesInView((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const index = Number((entry.target as HTMLElement).dataset.slideIndex);
            if (entry.isIntersecting) {
              next.add(index);
            }
          });
          return next;
        });
      },
      { root: container, rootMargin: '50px', threshold: 0 },
    );
    return () => {
      inViewObserverRef.current?.disconnect();
      inViewObserverRef.current = null;
    };
  }, []);

  const setSlideRef = useCallback(
    (index: number, isLast: boolean) => (el: HTMLElement | null) => {
      if (!el) return;
      el.dataset.slideIndex = String(index);
      inViewObserverRef.current?.observe(el);
      if (isLast) {
        (lastCardRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }
    },
    [],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!draggable || !containerRef.current) return;
      pointerDownRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        scrollLeft: containerRef.current.scrollLeft,
        pointerId: e.pointerId,
      };
    },
    [draggable],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;

      if (pointerDownRef.current && !dragStartRef.current) {
        const dx = e.clientX - pointerDownRef.current.clientX;
        const dy = e.clientY - pointerDownRef.current.clientY;
        if (
          Math.abs(dx) > DRAG_THRESHOLD_PX ||
          Math.abs(dy) > DRAG_THRESHOLD_PX
        ) {
          el.setPointerCapture(pointerDownRef.current.pointerId);
          dragStartRef.current = {
            clientX: e.clientX,
            scrollLeft: el.scrollLeft,
          };
          pointerDownRef.current = null;
          setIsDragging(true);
          e.preventDefault();
        }
      }

      if (dragStartRef.current) {
        e.preventDefault();
        const deltaX = e.clientX - dragStartRef.current.clientX;
        const isRtl = getComputedStyle(el).direction === 'rtl';
        const newScrollLeft = isRtl
          ? dragStartRef.current.scrollLeft + deltaX
          : dragStartRef.current.scrollLeft - deltaX;
        el.scrollLeft = newScrollLeft;
        dragStartRef.current = {
          clientX: e.clientX,
          scrollLeft: el.scrollLeft,
        };
      }
    },
    [],
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!containerRef.current) return;
    if (dragStartRef.current) {
      containerRef.current.releasePointerCapture(e.pointerId);
    }
    pointerDownRef.current = null;
    dragStartRef.current = null;
    setIsDragging(false);
  }, []);

  const selectedIndexInVisible = visibleSlides.findIndex(
    (option) => option.value === props.value,
  );

  useEffect(() => {
    if (selectedIndexInVisible < 0 || !containerRef.current) return;
    const container = containerRef.current;
    const slideEl = container.querySelector(
      `[data-slide-index="${selectedIndexInVisible}"]`,
    ) as HTMLElement | null;
    if (slideEl) {
      slideEl.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
    }
  }, [selectedIndexInVisible, visibleSlides.length, props.value]);

  const Input = props.multiSelect ? Checkbox : Radio;

  const handleGroupChange = useCallback(
    (value: string | string[]) => {
      if (props.multiSelect) {
        (props.onChange as (v: string[]) => void)(
          Array.isArray(value) ? value : [value],
        );
      } else {
        (props.onChange as (v: string) => void)(
          Array.isArray(value) ? value[0] ?? '' : value,
        );
      }
    },
    [props.multiSelect, props.onChange],
  );

  return (
    <Group className={classes['container']} gap={0} wrap="nowrap">
      <Button
        className={classes.button}
        disabled={!canScrollNext}
        onClick={onNextClick}
        size="compact-sm"
      >
        <IconChevronLeft />
      </Button>
      <Input.Group
        className={classes.radioGroup}
        dir="rtl"
        onChange={handleGroupChange}
        // @ts-expect-error Input.Group value is typed as intersection (string & string[]) when Input is Radio | Checkbox; we pass string or string[] at runtime
        value={
          props.multiSelect
            ? (props.value as string[])
            : (props.value as string)
        }
      >
        <section
          className={clsx(
            classes.embla,
            draggable && classes.emblaDraggable,
            isDragging && classes.emblaDragging,
          )}
          ref={containerRef}
          onPointerDown={draggable ? onPointerDown : undefined}
          onPointerMove={draggable ? onPointerMove : undefined}
          onPointerUp={draggable ? onPointerUp : undefined}
          onPointerCancel={draggable ? onPointerUp : undefined}
        >
          <div className={classes.embla__container}>
            {visibleSlides.map((option, index) => (
              <Menu
                closeDelay={100}
                disabled={subMode !== 'checkbox'}
                key={option.value}
                openDelay={100}
                trigger="hover"
              >
                <Menu.Target>
                  <Input.Card
                    className={clsx(classes.embla__slide, {
                      [classes['embla__slide--active']]:
                        option.value === props.value,
                      [classes.multiSelection]:
                        props.multiSelect && props.subValue !== option.value,
                      [classes.singleSelection]:
                        props.multiSelect && props.subValue === option.value,
                    })}
                    onContextMenu={(e) => {
                      if (props.multiSelect && props.onSubValueChange) {
                        e.preventDefault();
                        props.onSubValueChange(option.value);
                      }
                    }}
                    ref={setSlideRef(index, index === visibleSlides.length - 1)}
                    value={option.value}
                  >
                    <Stack gap="x2s" h="100%" pt="xs" px="xs">
                      <Box h="80%">
                        <LazyLoadImage
                          alt={option.valueLabel}
                          inView={slidesInView.has(index)}
                          index={0}
                          src={option.src}
                        />
                      </Box>
                      <Group flex="0 0 auto" gap="x2s" wrap="nowrap">
                        {subMode === 'checkbox' ? (
                          <Checkbox.Indicator
                            checked={
                              !props.multiSelect &&
                              props.subValue?.includes(option.value)
                            }
                            size="xs"
                          />
                        ) : (
                          <Input.Indicator size="xs" />
                        )}

                        <Text size="sm">
                          {option.valueLabel}
                          {option.manual_check && (
                            <span aria-label="changed" role="img">
                              {' '}
                              👁️‍🗨️{' '}
                            </span>
                          )}
                        </Text>
                      </Group>
                    </Stack>
                  </Input.Card>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={
                      <Checkbox.Indicator
                        checked={
                          !props.multiSelect &&
                          props.subValue?.includes(option.value)
                        }
                        size="xs"
                      />
                    }
                    onClick={() => {
                      if (!props.multiSelect) {
                        const draftList = props.subValue?.includes(option.value)
                          ? props.subValue.filter(
                              (value) => value !== option.value,
                            )
                          : [...(props.subValue || []), option.value];
                        props.onSubValueChange?.(draftList);
                      }
                    }}
                  >
                    <Text size="sm">Sélectionner la carte</Text>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ))}
          </div>
        </section>
      </Input.Group>
      <Button
        className={classes.button}
        disabled={!canScrollPrev}
        onClick={onPrevClick}
        size="compact-sm"
      >
        <IconChevronRight />
      </Button>
    </Group>
  );
}

export default SelectImageSlider;
